import { useState, useEffect } from 'react'
import api from '../services/api'
import { useEmpresa } from '../contexts/EmpresaContext'
import LookupSelect from '../components/LookupSelect'

interface ItemRecebido { linha: number; itsCod: number; descricao: string; quantidade: number }
interface ItemDevolvido {
  linha: number; itsCod: number; descricao: string; quantidade: number; valor: number
  devolucaoSerie: string; devolucaoNumero: number; devolucaoData: string | null
}
interface NotaComDevolucao {
  empcod: number; serie: string; numero: number; cliCod: string; fornecedor: string
  dataEmissao: string | null; dataRecebimento: string | null
  itensRecebidos: ItemRecebido[]; itensDevolvidos: ItemDevolvido[]
}

function hojeISO() { return new Date().toISOString().slice(0, 10) }
function doisMesesAtrasISO() {
  const d = new Date()
  d.setMonth(d.getMonth() - 2)
  return d.toISOString().slice(0, 10)
}
function fmtData(v: string | null) { return v ? v.slice(0, 10).split('-').reverse().join('/') : '-' }
function fmtQtd(v: number) { return (v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 3, maximumFractionDigits: 3 }) }

const inp: React.CSSProperties = {
  border: '1px solid #d1d5db', borderRadius: 4, padding: '4px 8px',
  fontSize: '0.85rem', height: 32, background: '#fff',
}
const label: React.CSSProperties = { fontSize: '0.72rem', color: '#6b7280', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.03em' }

function Campo({ rotulo, width, children }: { rotulo: string; width?: number; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3, width }}>
      <label style={label}>{rotulo}</label>
      {children}
    </div>
  )
}

export default function RelatorioItensDevolvidos() {
  const { empresaCod } = useEmpresa()
  const [empcodDe, setEmpcodDe] = useState(String(empresaCod ?? 1))
  const [empcodAte, setEmpcodAte] = useState(String(empresaCod ?? 1))
  const [dataDe, setDataDe] = useState(doisMesesAtrasISO())
  const [dataAte, setDataAte] = useState(hojeISO())
  const [classificacaoPor, setClassificacaoPor] = useState<'recebimento' | 'emissao'>('recebimento')
  const [fornecedorCod, setFornecedorCod] = useState('')
  const [fornecedorDes, setFornecedorDes] = useState('')
  const [itsCodDe, setItsCodDe] = useState('')
  const [itsCodAte, setItsCodAte] = useState('')
  const [exibir, setExibir] = useState<'todas' | 'comDevolucao'>('comDevolucao')
  const [listarItens, setListarItens] = useState(true)

  // "Todas as Recebidas" sem listar item nenhum não mostra nada útil (só o
  // cabeçalho da nota) — trava "Listar Itens" em Sim nesse modo.
  useEffect(() => {
    if (exibir === 'todas') setListarItens(true)
  }, [exibir])

  const [carregando, setCarregando] = useState(false)
  const [gerandoPdf, setGerandoPdf] = useState(false)
  const [erro, setErro] = useState('')
  const [notas, setNotas] = useState<NotaComDevolucao[] | null>(null)

  function filtrosParams() {
    return {
      empcodDe: empcodDe || undefined, empcodAte: empcodAte || undefined,
      dataDe: dataDe || undefined, dataAte: dataAte || undefined,
      classificacaoPor, cliCodDe: fornecedorCod || undefined, cliCodAte: fornecedorCod || undefined,
      itsCodDe: itsCodDe || undefined, itsCodAte: itsCodAte || undefined,
      exibir,
    }
  }

  async function gerar() {
    setCarregando(true); setErro(''); setNotas(null)
    try {
      const r = await api.get('/relatorios-compras/itens-devolvidos', { params: filtrosParams() })
      setNotas(r.data)
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Erro ao gerar relatório.')
    } finally {
      setCarregando(false)
    }
  }

  async function gerarPdf() {
    setGerandoPdf(true); setErro('')
    try {
      const r = await api.get('/relatorios-compras/itens-devolvidos/pdf', {
        params: { ...filtrosParams(), listarItens: listarItens ? 'S' : 'N' },
        responseType: 'blob',
      })
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }))
      window.open(url, '_blank')
    } catch {
      setErro('Erro ao gerar PDF.')
    } finally {
      setGerandoPdf(false)
    }
  }

  const totalItensRec = (notas ?? []).reduce((s, n) => s + n.itensRecebidos.length, 0)
  const totalItensDev = (notas ?? []).reduce((s, n) => s + n.itensDevolvidos.length, 0)
  const totalQtdRec = (notas ?? []).reduce((s, n) => s + n.itensRecebidos.reduce((a, it) => a + it.quantidade, 0), 0)
  const totalQtdDev = (notas ?? []).reduce((s, n) => s + n.itensDevolvidos.reduce((a, it) => a + it.quantidade, 0), 0)

  return (
    <div>
      <h2 style={{ fontSize: '1.1rem', fontWeight: 700, margin: '0 0 0.75rem' }}>
        Itens Devolvidos / Rejeitados
      </h2>

      <div style={{
        background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
        boxShadow: '0 1px 4px rgba(0,0,0,0.07)', padding: '1rem 1.1rem', marginBottom: '1rem',
      }}>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '0.75rem' }}>
          <Campo rotulo="Empresa De" width={90}>
            <input style={inp} inputMode="numeric" value={empcodDe} onChange={e => setEmpcodDe(e.target.value.replace(/\D/g, ''))} />
          </Campo>
          <Campo rotulo="Até" width={90}>
            <input style={inp} inputMode="numeric" value={empcodAte} onChange={e => setEmpcodAte(e.target.value.replace(/\D/g, ''))} />
          </Campo>
          <Campo rotulo="Período De" width={150}>
            <input style={inp} type="date" value={dataDe} onChange={e => setDataDe(e.target.value)} />
          </Campo>
          <Campo rotulo="Até" width={150}>
            <input style={inp} type="date" value={dataAte} onChange={e => setDataAte(e.target.value)} />
          </Campo>
          <Campo rotulo="Tipo (Data)" width={150}>
            <select style={inp} value={classificacaoPor} onChange={e => setClassificacaoPor(e.target.value as any)}>
              <option value="recebimento">Data de Recebimento</option>
              <option value="emissao">Data de Emissão</option>
            </select>
          </Campo>
          <Campo rotulo="Fornecedor (opcional)" width={260}>
            <LookupSelect
              endpoint="/erp/clientes/lista" codKey="cli_cod" descKey="cli_fan"
              codValue={fornecedorCod} descValue={fornecedorDes}
              onSelect={(cod, des) => { setFornecedorCod(cod); setFornecedorDes(des) }}
              placeholder="Todos" listWidth={320} somenteComTexto
            />
          </Campo>
        </div>

        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <Campo rotulo="Item De (opcional)" width={110}>
            <input style={inp} inputMode="numeric" value={itsCodDe} onChange={e => setItsCodDe(e.target.value.replace(/\D/g, ''))} />
          </Campo>
          <Campo rotulo="Até" width={110}>
            <input style={inp} inputMode="numeric" value={itsCodAte} onChange={e => setItsCodAte(e.target.value.replace(/\D/g, ''))} />
          </Campo>
          <Campo rotulo="Exibir" width={200}>
            <select style={inp} value={exibir} onChange={e => setExibir(e.target.value as any)}>
              <option value="comDevolucao">Recebidas com Devoluções</option>
              <option value="todas">Todas as Recebidas</option>
            </select>
          </Campo>
          <Campo rotulo="Listar Itens" width={130}>
            <select style={inp} value={listarItens ? 'S' : 'N'} disabled={exibir === 'todas'}
              onChange={e => setListarItens(e.target.value === 'S')}>
              <option value="S">Sim</option>
              <option value="N">Não</option>
            </select>
          </Campo>
          <button className="btn btn-primary btn-sm" disabled={carregando} onClick={gerar} style={{ height: 32 }}>
            {carregando ? 'Gerando...' : 'Gerar Relatório'}
          </button>
        </div>
      </div>

      {erro && <div className="alert alert-danger" style={{ marginBottom: '0.75rem' }}>{erro}</div>}

      {notas && (
        <div style={{
          background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
          boxShadow: '0 1px 4px rgba(0,0,0,0.07)', padding: '1rem 1.1rem',
        }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: '0.9rem' }}>
            <button className="btn btn-sm" disabled={gerandoPdf} onClick={gerarPdf} style={{ height: 32 }}>
              {gerandoPdf ? 'Gerando...' : '📄 Gerar PDF'}
            </button>
          </div>

          {notas.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>Nenhuma nota encontrada.</div>
          ) : (
            <>
              {notas.map(n => (
                <div key={`${n.empcod}-${n.serie}-${n.numero}-${n.cliCod}`} style={{ marginBottom: '1.25rem', border: '1px solid #e5e7eb', borderRadius: 6, overflow: 'hidden' }}>
                  <div style={{
                    display: 'grid', gridTemplateColumns: '1.6fr 0.5fr 0.5fr 0.7fr 0.8fr 0.8fr 0.8fr 0.8fr',
                    gap: '0.5rem', padding: '0.5rem 0.75rem', background: '#1b3f7b', color: '#fff', fontSize: '0.78rem', alignItems: 'center',
                  }}>
                    <div><strong>{n.cliCod}</strong> — {n.fornecedor}</div>
                    <div>Est. {n.empcod}</div>
                    <div>Série {n.serie}</div>
                    <div>Nº {n.numero}</div>
                    <div>Emi. {fmtData(n.dataEmissao)}</div>
                    <div>Receb. {fmtData(n.dataRecebimento)}</div>
                    <div>Recebidos: {n.itensRecebidos.length}</div>
                    <div style={{ fontWeight: 700, color: n.itensDevolvidos.length ? '#fecaca' : '#fff' }}>Devolvidos: {n.itensDevolvidos.length}</div>
                  </div>

                  {listarItens && (
                    <div style={{ padding: '0.6rem 0.75rem' }}>
                      {n.itensRecebidos.length > 0 && (
                        <div style={{ marginBottom: '0.6rem' }}>
                          <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#374151', textTransform: 'uppercase', marginBottom: 3 }}>Itens Recebidos</div>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                            <thead>
                              <tr style={{ background: '#f9fafb' }}>
                                <th style={{ textAlign: 'left', padding: '2px 5px', width: 40 }}>Linha</th>
                                <th style={{ textAlign: 'left', padding: '2px 5px', width: 70 }}>Item</th>
                                <th style={{ textAlign: 'left', padding: '2px 5px' }}>Descrição</th>
                                <th style={{ textAlign: 'right', padding: '2px 5px', width: 100 }}>Qtd Receb.</th>
                              </tr>
                            </thead>
                            <tbody>
                              {n.itensRecebidos.map(it => (
                                <tr key={it.linha}>
                                  <td style={{ padding: '2px 5px' }}>{it.linha}</td>
                                  <td style={{ padding: '2px 5px' }}>{it.itsCod}</td>
                                  <td style={{ padding: '2px 5px' }}>{it.descricao}</td>
                                  <td style={{ padding: '2px 5px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtQtd(it.quantidade)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                      {n.itensDevolvidos.length > 0 && (
                        <div>
                          <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#7f1d1d', textTransform: 'uppercase', marginBottom: 3 }}>Itens Devolvidos</div>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                            <thead>
                              <tr style={{ background: '#fef2f2' }}>
                                <th style={{ textAlign: 'left', padding: '2px 5px', width: 40 }}>Linha</th>
                                <th style={{ textAlign: 'left', padding: '2px 5px', width: 70 }}>Item</th>
                                <th style={{ textAlign: 'left', padding: '2px 5px' }}>Descrição</th>
                                <th style={{ textAlign: 'right', padding: '2px 5px', width: 100 }}>Qtd Devolv.</th>
                                <th style={{ textAlign: 'center', padding: '2px 5px', width: 130 }}>Nota Devolução</th>
                              </tr>
                            </thead>
                            <tbody>
                              {n.itensDevolvidos.map((it, idx) => (
                                <tr key={`${it.linha}-${idx}`}>
                                  <td style={{ padding: '2px 5px' }}>{it.linha}</td>
                                  <td style={{ padding: '2px 5px' }}>{it.itsCod}</td>
                                  <td style={{ padding: '2px 5px' }}>{it.descricao}</td>
                                  <td style={{ padding: '2px 5px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: '#b91c1c', fontWeight: 600 }}>{fmtQtd(it.quantidade)}</td>
                                  <td style={{ padding: '2px 5px', textAlign: 'center', color: '#6b7280' }}>
                                    {it.devolucaoSerie}/{it.devolucaoNumero} — {fmtData(it.devolucaoData)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}

              <div style={{ border: '2px solid #111', borderRadius: 6, padding: '0.75rem 1rem', marginTop: '1rem' }}>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>TOTAL GERAL</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.4rem 1.5rem', fontSize: '0.85rem' }}>
                  <div>Itens Recebidos no Período: <strong>{totalItensRec}</strong></div>
                  <div>Quantidade Recebida: <strong>{fmtQtd(totalQtdRec)}</strong></div>
                  <div>Itens Devolvidos no Período: <strong>{totalItensDev}</strong></div>
                  <div>Quantidade Devolvida: <strong>{fmtQtd(totalQtdDev)}</strong></div>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}
